import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  ChangeDetectorRef,
} from '@angular/core';
import { FooterButtonStates } from '@app/onboarding/components/onboarding-footer/onboarding-footer.model';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { OnboardingConfigService } from '@app/onboarding/services/onboarding-config.service';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { NotificationType } from '@lib/fresco';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

@Component({
  selector: 'dgx-assigned-skills',
  templateUrl: './assigned-skills.component.html',
  styleUrls: ['./assigned-skills.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class AssignedSkillsComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public readonly NotificationType = NotificationType;

  public templatePrefix: string;
  public assignedTags$: Observable<TagsApi.Tag[]>;
  public showErrorNoSelectedSkills = false;
  public showWarningSkillRemoved = false;
  public description: string;
  public title: string;
  public i18n = this.translateService.instant([
    'OnboardingFooter_PreviousStep',
    'OnboardingFooter_NextStep',
    'Onboarding_AssignedSkillsHeader',
    'Onboarding_AssignedSkillsInformationTooltip',
    'Onboarding_AssignedSkillsWarning',
    'Onboarding_AssignedSkillsError',
  ]);
  public orgName = this.authService.authUser?.defaultOrgInfo
    ? this.authService.authUser.defaultOrgInfo.name
    : 'Degreed';
  public currentRoleTitle: string;
  private footerButtonStates: FooterButtonStates;

  constructor(
    private translateService: TranslateService,
    private onboardingConfigService: OnboardingConfigService,
    private onboardingStateService: OnboardingStateService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private trackerService: TrackerService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.assignedTags$ = this.onboardingStateService.assignedTags$;
    this.footerButtonStates = {
      currentStep: OnboardingStep.assigned,
      disableNextStep: false,
      previousButton: this.i18n.OnboardingFooter_PreviousStep,
      nextButton: this.i18n.OnboardingFooter_NextStep,
    };
    this.onboardingConfigService.footerButtonStateChange(
      this.footerButtonStates
    );

    this.onboardingConfigService.config.subscribe((config) => {
      this.templatePrefix = config.templatePrefix;
      this.currentRoleTitle = this.onboardingStateService.currentRole?.title
        ? this.onboardingStateService.currentRole?.title
        : '';

      this.onboardingStateService.updateTrackerStep(
        this.onboardingConfigService.getTrackerSteps(
          this.templatePrefix,
          OnboardingStep.assigned,
          !this.showErrorNoSelectedSkills
        )
      );
      this.cdr.detectChanges();
    });
    this.onboardingStateService.currentStepChange(OnboardingStep.assigned);
  }

  /**
   * Update the list of tags, nad display a warning if one of the assigned skills is removed.
   * Display an error if all are removed.
   *
   * @param tag tag that has been toggled.
   * @param tags List of all assigned tags
   */
  public onToggleTag(tag: TagsApi.Tag, tags: TagsApi.Tag[]) {
    tag.isFollowing = !tag.isFollowing;
    // Update stream of assigned skills
    this.onboardingStateService.assignedTagsChange(tags);
    const isAnySelected = this.isAnySelected(tags);
    this.showErrorNoSelectedSkills = !isAnySelected;
    const nonSelectedTags = tags.filter((tag) => !tag.isFollowing);
    this.showWarningSkillRemoved =
      nonSelectedTags?.length > 0 && !this.showErrorNoSelectedSkills;
    this.updateFooterStates(this.showErrorNoSelectedSkills);
    this.trackerService.trackEventData({
      action: tag.isFollowing
        ? 'Onboarding Skill Selected'
        : 'Onboarding Skill Deselected',
      category: 'Onboarding',
      properties: {
        Location: 'Onboarding Assigned Skills',
        SkillId: tag.tagId,
        SkillName: tag.name,
      },
    });
  }

  private updateFooterStates(isDisabled: boolean) {
    this.onboardingConfigService.footerButtonStateChange({
      ...this.footerButtonStates,
      disableNextStep: isDisabled,
    });

    this.onboardingStateService.updateTrackerStep(
      this.onboardingConfigService.getTrackerSteps(
        this.templatePrefix,
        OnboardingStep.assigned,
        !isDisabled
      )
    );
  }

  private isAnySelected(tags: TagsApi.Tag[]): boolean {
    return !!tags.find((tag) => {
      return tag.isFollowing;
    });
  }
}
